import { createFeature, createReducer, on } from '@ngrx/store'
import { ViewerState, LayerConfig, LayerGroup } from './viewer.state'
import { viewerActions } from './viewer.actions'
import { caseActions } from '../case/case.actions'
import { isSelectionAnnotationLayer } from './viewer-layer-utils'
import { mergeStaticAndDynamicLayer, getLargeGroupLayerToGroupIdMap } from './viewer-state-utils'

const mergeLayerWithQueryParams = (
    configLayer: LayerConfig,
    queryLayers: LayerConfig[] | undefined
): LayerConfig => {
    const paramLayer = queryLayers?.find(paramLayer => paramLayer.id === configLayer.id)
    // Saved API state wins over URL params so case reload restores persisted viewstate.
    return paramLayer ? { ...paramLayer, ...configLayer } : configLayer
}

const createInitialState = (): ViewerState => ({
    dimensions: undefined,
    crossSectionScale: undefined,
    layout: undefined,
    position: undefined,
    layers: [],
    layerGroups: [],
    expandedNodes: [],
    invisibleLayers: [],
    favoriteLayers: [],
    favoriteLayersFieldPresent: false,
    explicitFavoriteLayers: [],
    persistedDynamicLayers: [],
    hasPersistedLayerStateInApi: false,
    originalLayers: [],
    searchKey: '',
    layerListDialogOpen: false,
})

export const viewerFeature = createFeature({
    name: 'viewer',
    reducer: createReducer<ViewerState>(
        createInitialState(),
        on(viewerActions.configLoaded, (state, { config, queryParams }): ViewerState => {
            const persistedDynamicById = new Map(
                (config?.persistedDynamicLayers ?? [])
                    .filter((layer): layer is LayerConfig & { id: string } => !!layer.id)
                    .map(layer => [layer.id, layer])
            )
            const applyPersistedSettings = (layer: LayerConfig): LayerConfig => {
                if (!layer.id) {
                    return layer
                }
                const persisted = persistedDynamicById.get(layer.id)
                return persisted ? mergeStaticAndDynamicLayer(layer, persisted) : layer
            }
            const layersWithState =
                config?.layers?.map(configLayer =>
                    applyPersistedSettings(
                        mergeLayerWithQueryParams(configLayer, queryParams.layers)
                    )
                ) ?? []
            const visibleLayers: LayerConfig[] = []
            const invisibleLayers: LayerConfig[] = []
            const largeGroupLayerToGroupId = getLargeGroupLayerToGroupIdMap(config!.layerGroups)
            const savedFavorites = config?.favoriteLayers ?? []
            const favoritedLayerIds = new Set(savedFavorites)
            const isConfiguredEmpty =
                (config?.favoriteLayersFieldPresent &&
                    (config?.explicitFavoriteLayers?.length ?? 0) === 0) ||
                (!config?.favoriteLayersFieldPresent && !config?.hasPersistedLayerStateInApi)
            const isInitialLoad =
                !isConfiguredEmpty &&
                !config?.favoriteLayersFieldPresent &&
                !config?.hasPersistedLayerStateInApi &&
                savedFavorites.length === 0
            const smallGroupLayerIds = new Set(
                layersWithState
                    .filter(
                        l =>
                            l.id &&
                            !isSelectionAnnotationLayer(l) &&
                            !largeGroupLayerToGroupId.has(l.id)
                    )
                    .map(l => l.id!)
            )
            const explicitFavoriteLayers = config?.explicitFavoriteLayers ?? []
            // Backward compat: old saves only persisted large-group IDs in favoriteLayers.
            const legacyAutoLoadSmallGroups =
                !config?.favoriteLayersFieldPresent &&
                explicitFavoriteLayers.length > 0 &&
                !explicitFavoriteLayers.some(id => smallGroupLayerIds.has(id))

            layersWithState.forEach(l => {
                if (isSelectionAnnotationLayer(l)) {
                    // Always keep selection COM annotation layers loaded and visible.
                    visibleLayers.push({ ...applyPersistedSettings(l), visible: true })
                    return
                }
                if (!l.id) {
                    visibleLayers.push(applyPersistedSettings(l))
                    return
                }

                const largeGroupId = l.id ? largeGroupLayerToGroupId.get(l.id) : undefined
                const isLargeGroupLayer = !!largeGroupId

                if (favoritedLayerIds.has(l.id)) {
                    visibleLayers.push(applyPersistedSettings(l))
                    return
                }

                if (isLargeGroupLayer) {
                    invisibleLayers.push({
                        ...applyPersistedSettings(l),
                        groupId: largeGroupId,
                    })
                    return
                }

                if (isInitialLoad || legacyAutoLoadSmallGroups) {
                    favoritedLayerIds.add(l.id)
                    visibleLayers.push({
                        ...applyPersistedSettings(l),
                        visible: false,
                    })
                    return
                }

                invisibleLayers.push({
                    ...applyPersistedSettings(l),
                    visible: false,
                    groupId: findGroupIdForLayerId(l.id, config!.layerGroups),
                } as LayerConfig)
            })
            return config
                ? {
                      ...state,
                      ...config,
                      position: queryParams.position ?? config.position,
                      crossSectionScale: queryParams.zoom ?? config.crossSectionScale,
                      layers: visibleLayers,
                      invisibleLayers: invisibleLayers,
                      favoriteLayers:
                          isInitialLoad || legacyAutoLoadSmallGroups
                              ? [...favoritedLayerIds]
                              : savedFavorites,
                  }
                : state
        }),
        on(
            viewerActions.setFavoriteLayers,
            (state, { favoriteLayers }): ViewerState => ({
                ...state,
                favoriteLayers,
            })
        ),
        on(viewerActions.setZoom, (state, { zoom }): ViewerState => {
            if (zoom === 0) {
                console.error('bug: attempt to set zoom to zero (setZoom)')
                zoom = 1
            }
            return {
                ...state,
                crossSectionScale: zoom,
            }
        }),
        on(viewerActions.setPosition, (state, { position }): ViewerState => {
            return {
                ...state,
                position,
            }
        }),
        on(viewerActions.setExpandedNodes, (state, { expandedNodes }): ViewerState => {
            return {
                ...state,
                expandedNodes,
            }
        }),
        on(viewerActions.setLayout, (state, { layout }): ViewerState => {
            return {
                ...state,
                layout,
            }
        }),
        on(viewerActions.setLayerName, (state, { name, newName }): ViewerState => {
            return {
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              name: newName,
                          }
                ),
            }
        }),
        on(
            viewerActions.setLayerVisibility,
            (state, { name, visible }): ViewerState => {
                const target = state.layers?.find(l => l.name === name)
                // Selection COM layers stay visible; ignore hide requests from layer sync.
                if (target && isSelectionAnnotationLayer(target) && !visible) {
                    return state
                }
                return {
                    ...state,
                    layers: state.layers?.map(l =>
                        l.name !== name
                            ? l
                            : {
                                  ...l,
                                  visible,
                              }
                    ),
                }
            }
        ),
        on(viewerActions.setLocalLayerVisibility, (state, { id, visible }): ViewerState => {
            const layerInState =
                state.layers?.find(l => l.id === id) ??
                state.invisibleLayers?.find(l => l.id === id)
            if (layerInState && isSelectionAnnotationLayer(layerInState)) {
                return state
            }
            const currentFavorites = state.favoriteLayers ?? []
            if (visible) {
                const layer = state.invisibleLayers?.find(l => l.id === id)
                if (!layer) return state
                const shaderColor = getRandomColor()
                const favoriteLayers = currentFavorites.includes(id)
                    ? currentFavorites
                    : [...currentFavorites, id]
                return {
                    ...state,
                    invisibleLayers: state.invisibleLayers?.filter(l => l.id !== id),
                    layers: [
                        ...state.layers!,
                        { ...layer, visible: false, blend: 'additive', shaderColor }!,
                    ],
                    favoriteLayers,
                }
            } else {
                const layer = state.layers?.find(l => l.id === id)
                if (!layer) return state
                const groupId = layer.groupId ?? findGroupIdForLayerId(id, state.layerGroups)
                const favoriteLayers = currentFavorites.filter(lid => lid !== id)
                return {
                    ...state,
                    layers: state.layers!.filter(l => l.id !== id),
                    invisibleLayers: [
                        ...(state.invisibleLayers ?? []),
                        { ...layer, visible: false, groupId } as LayerConfig,
                    ],
                    favoriteLayers,
                }
            }

            function getRandomColor() {
                const components = ['00', 'FF']
                let color
                do {
                    const r = components[Math.floor(Math.random() * 2)]
                    const g = components[Math.floor(Math.random() * 2)]
                    const b = components[Math.floor(Math.random() * 2)]
                    color = `#${r}${g}${b}`
                } while (color === '#FFFFFF' || color === '#000000')
                return color
            }
        }),
        on(
            viewerActions.setLayerOpacity,
            (state, { name, opacity }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              opacity,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerOpacityUnselected,
            (state, { name, opacity }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              opacityUnselected: opacity,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerSaturation,
            (state, { name, saturation }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              saturation,
                          }
                ),
            })
        ),
        on(viewerActions.setLayerShaderColor, (state, { name, shaderColor }): ViewerState => {
            const l = state.layers?.find(l => l.name === name)
            return !!l && l.shaderColor !== shaderColor
                ? {
                      ...state,
                      layers: state.layers?.map(l =>
                          l.name !== name
                              ? l
                              : {
                                    ...l,
                                    shaderColor,
                                }
                      ),
                  }
                : state
        }),
        on(
            viewerActions.setLayerBrightness,
            (state, { name, brightness }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              brightness,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerContrast,
            (state, { name, contrast }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              contrast,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerNormalize,
            (state, { name, normalize }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              normalize,
                          }
                ),
            })
        ),
        on(
            viewerActions.setLayerSegmentationColor,
            (state, { name, segmentationColor }): ViewerState => ({
                ...state,
                layers: state.layers?.map(l =>
                    l.name !== name
                        ? l
                        : {
                              ...l,
                              segmentationColor,
                          }
                ),
            })
        ),
        on(viewerActions.setLayerListDialogOpen, (state, { open }): ViewerState => ({
            ...state,
            layerListDialogOpen: open,
        })),
        on(viewerActions.setSearchKey, (state, { searchKey }): ViewerState => {
            return {
                ...state,
                searchKey,
            }
        }),
        on(viewerActions.deleteLayer, (state, { name }): ViewerState => {
            return {
                ...state,
                layers: state.layers?.filter(l => l.name !== name),
            }
        }),
        on(caseActions.loadCase, (): ViewerState => createInitialState())
    ),
})

function findGroupIdForLayerId(layerId: string, layerGroups: LayerGroup[]): string | undefined {
    for (const group of layerGroups) {
        if (group.layerIds?.includes(layerId)) return group.id
        const found = findGroupIdForLayerId(layerId, group.children ?? [])
        if (found) return found
    }
    return undefined
}

export const { reducer } = viewerFeature
